<script setup>
import {reactive, onMounted} from "vue";
import {ApiWrapper} from "@/Utils/ApiWrapper";
import {Permissions} from "@/Utils/Permissions";
import {ToastUtil} from "@/Utils/ToastUtil";

const data = reactive({
  loading: false,
  generatingReport: false,
  dates: [],
  search: null,
  newRule: false,
  newClientId: null,
  newPerson: null,
  newPercent: null,
  newId: null,
  headers: [
    { title: 'Name', key: 'name' },
    { title: 'Client', key: 'company' },
    { title: 'Percent', key: 'percent' },
    { title: 'Actions', key: 'actions' },
  ],
  items: [],
  clients: [],
});

const downloadCSV = () => {
  if(data.dates.length < 2){
    ToastUtil.errorMsg('Please select a date range')
  }
  else{
    if(data.dates.length > 2){
      let minDate=new Date(Math.min.apply(null,data.dates));
      let maxDate=new Date(Math.max.apply(null,data.dates));
      data.dates=[minDate,maxDate];
    }
    const dates = data.dates.map((d) => d.toLocaleDateString());

    data.loading = true
    ApiWrapper.post('marketing', 'get-pay', {
          dates: dates
        })
        .then((response) => {
          let csvContent = 'data:text/csv;charset=utf-8,'
          csvContent += response.csvData;
          const fileData = encodeURI(csvContent);
          const link = document.createElement('a');
          link.setAttribute('href', fileData);
          link.setAttribute('download', 'MarketingPay_' + dates[0] + '~' + dates[1] +'.csv');
          link.click();
        })
        .catch((e)=>{
          ToastUtil.error(e);
        })
        .finally(()=>{
          data.loading = false
        })
  }
}

const createDownload = (csvData) => {

}

const getData = () => {
  ApiWrapper.post('marketing', 'get-rules', {
      })
      .then((response) => {
        if(response.items){
          data.items = response.items
        }
        if(response.clients){
          data.clients = response.clients
        }
      })
      .catch((e)=>{
        ToastUtil.error(e);
      })
      .finally(()=>{
        data.loading = false;
      })
}

const addRule = () => {
  data.loading = true
  ApiWrapper.post('marketing', 'add-rule', {
        newPercent: data.newPercent,
        newClientId: data.newClientId,
        newPerson: data.newPerson
      })
      .then((response) => {
        getData()
        data.newRule = false
        ToastUtil.successMsg('Added marketing rule');
      })
      .catch((err) => {
        ToastUtil.error(err);
        data.loading = false;
      })
}

const removeRule = (item) => {
  data.loading = true
  ApiWrapper.post('marketing', 'delete-rule', {
        id: item.id,
      })
      .then((response) => {
        getData()
        ToastUtil.successMsg('Deleted marketing rule');
      })
      .catch((err) => {
        ToastUtil.error(err);
        data.loading = false;
      })
}

onMounted(()=>{
  getData()
})
</script>

<template>
  <v-container fluid>
    <v-card class="elevation-2" height="auto" width="auto">
      <v-toolbar flat color="white">
        <v-toolbar-title>Marketing Rules</v-toolbar-title>
        <v-progress-linear
            :active="data.loading"
            :indeterminate="data.loading"
            absolute
            bottom
            color="primary"
        ></v-progress-linear>
        <v-divider class="mx-4" inset vertical></v-divider>

        <v-text-field
            v-model="data.search"
            append-inner-icon="mdi-magnify"
            label="Search"
            single-line
            hide-details
            class="px-10"
        ></v-text-field>

        <v-spacer></v-spacer>
        <div class="mx-2">
          <v-btn variant="elevated" color="primary" v-if="Permissions.checkPermissions(['marketing.get-pay'])" @click="data.generatingReport=true">
            Generate Marketing Pay Report
          </v-btn>
        </div>


        <v-btn v-if="Permissions.checkPermissions(['marketing.new-rule'])" size="small" variant="elevated" color="primary" @click="data.newRule = true">
          New Rule
        </v-btn>
      </v-toolbar>
      <v-row v-if="data.generatingReport">
        <v-col cols="12" sm="6">
          <v-layout justify-center>
            <v-date-picker
                v-model="data.dates"
                multiple="range"
            ></v-date-picker>
          </v-layout>
        </v-col>

        <v-col cols="12" sm="6">
          <v-btn
              size="small"
              variant="elevated"
              class="px-2"
              @click="data.generatingReport=false; downloadCSV()"
              color="primary"
          >
            Download
          </v-btn>
          <v-btn
              size="small"
              variant="elevated"
              class="px-2"
              @click="data.generatingReport=false;"
              color="error"
          >
            Cancel
          </v-btn>
        </v-col>
      </v-row>
      <v-row v-if="data.newRule" class="px-5">
        <v-col cols="12" sm="3">
          <v-text-field v-model="data.newPerson" label="Name"></v-text-field>
        </v-col>

        <v-col cols="12" sm="3">
          <v-autocomplete
              class="px-3"
              v-model="data.newClientId"
              :items="data.clients"
              :rules="[v => !!v || 'A Client is required']"
              label="Type or Select Clients's Name"
              item-title="text"
              item-value="value"
              clearable
          ></v-autocomplete>
        </v-col>

        <v-col cols="12" sm="3">
          <v-text-field label="Enter a Percent" v-model="data.newPercent"></v-text-field>
        </v-col>

        <v-col cols="12" sm="4">
          <v-btn
              size="small"
              variant="elevated"
              class="px-2"
              @click="addRule()"
              color="primary"
          >
            <v-icon color="white">mdi-plus</v-icon>
          </v-btn>
          <v-btn
              size="small"
              variant="elevated"
              class="px-2"
              @click="data.newRule=false"
              color="error"
          >
            <v-icon color="white">mdi-close</v-icon>
          </v-btn>
        </v-col>
      </v-row>


      <v-data-table
          :items="data.items"
          :headers="data.headers"
          :search="data.search"
          :loading="data.loading"
          loading-text="Loading... Please wait"
      >
        <template v-slot:[`item.personname`]="{ item }">
          <RouterLink :to="`/userprofile/${item.personid}`">
            {{ item.personname }}
          </RouterLink>
        </template>
        <template v-slot:[`item.clientname`]="{ item }">
          <RouterLink :to="`/client/${item.clientid}`">
            {{ item.clientname }}
          </RouterLink>
        </template>

        <template v-slot:[`item.actions`]="{ item }">
          <!-- <v-icon @click="removeRule(item)"> mdi-pencil </v-icon> -->
          <v-icon v-if="Permissions.checkPermissions(['marketing.delete-rule'])" @click="removeRule(item)"> mdi-delete </v-icon>
        </template>
      </v-data-table>
    </v-card>
  </v-container>
</template>

<style scoped>
.container {
  padding: 5px;
}
[v-cloak] {
  display: none;
}
</style>